import React from 'react';
import { GitCommitHorizontal } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * RoundTripBadge — Badge unifié "Aller-retour".
 *
 * Variants :
 *  - 'compact' (défaut) : pour les listes — "A/R" + petite distance
 *  - 'full' : pour les fiches/formulaires — "Aller-retour" + distance complète
 *
 * Couleur unifiée : violet (purple-100/700) — cohérent avec le flow de création (RoundTripSection).
 *
 * Props :
 *  - distanceKm (number, optional) — si fourni, affiche "· Xkm"
 *  - returnCargoMode (string, optional) — 'empty' affiche "· Retour à vide" en title
 *  - variant ('compact' | 'full', défaut 'compact')
 *  - className (string)
 *  - testId (string)
 */
export const RoundTripBadge = ({ distanceKm, returnCargoMode, variant = 'compact', className, testId = 'badge-round-trip' }) => {
  const isCompact = variant === 'compact';
  const distanceLabel = distanceKm ? `${Math.round(distanceKm)} km${isCompact ? '' : ' total'}` : null;
  const titleParts = [
    'Aller-retour',
    distanceLabel,
    returnCargoMode === 'empty' ? 'Retour à vide' : null,
    returnCargoMode === 'different' ? 'Marchandise différente au retour' : null,
  ].filter(Boolean);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-semibold border',
        'bg-purple-100 text-purple-700 border-purple-200',
        isCompact ? 'px-1.5 h-5 text-[10px]' : 'px-2.5 py-1 text-xs',
        className
      )}
      title={titleParts.join(' · ')}
      data-testid={testId}
    >
      <GitCommitHorizontal className={isCompact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      {isCompact ? 'A/R' : 'Aller-retour'}
      {distanceLabel && (
        <span className={cn('font-normal', isCompact ? 'text-[9px]' : 'text-[11px]')}>
          · {distanceLabel}
        </span>
      )}
    </span>
  );
};

export default RoundTripBadge;
